import { Component, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';

interface QuizQuestion {
  id: number;
  questionKey: string;
  explanationKey: string;
  options: { key: string; value: number }[];
  correctAnswer: number;
}

/**
 * Visual state of an option in the question screen. Answering reveals the
 * correction, so the answer is then locked: only 'selectable' is clickable.
 */
type OptionState = 'selectable' | 'correct' | 'incorrect' | 'dimmed';

const OPTION_STATE_CLASSES: Record<OptionState, string> = {
  selectable: 'cursor-pointer border-greyscale-200 bg-white hover:border-primary-300',
  correct: 'cursor-default border-primary-500 bg-primary-50',
  incorrect: 'cursor-default border-error-500 bg-error-50',
  dimmed: 'cursor-default border-greyscale-200 bg-white opacity-60',
};

@Component({
  selector: 'app-quiz',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    ButtonModule,
    RadioButtonModule,
    FormsModule,
    LucideAngularModule,
  ],
  templateUrl: './quiz.component.html'
})
export class QuizComponent {
  // Display order defined by the content review: 1 - 10 - 3 - 6 - 5 - 2 - 7 - 4 - 9 - 8
  questions: QuizQuestion[] = [
    {
      id: 1,
      questionKey: 'help.onboarding.quiz.questions.q1.question',
      explanationKey: 'help.onboarding.quiz.questions.q1.explanation',
      options: [
        { key: 'help.onboarding.quiz.questions.q1.options.a', value: 0 },
        { key: 'help.onboarding.quiz.questions.q1.options.b', value: 1 },
        { key: 'help.onboarding.quiz.questions.q1.options.c', value: 2 },
        { key: 'help.onboarding.quiz.questions.q1.options.d', value: 3 },
      ],
      correctAnswer: 1
    },
    {
      id: 10,
      questionKey: 'help.onboarding.quiz.questions.q10.question',
      explanationKey: 'help.onboarding.quiz.questions.q10.explanation',
      options: [
        { key: 'help.onboarding.quiz.questions.q10.options.a', value: 0 },
        { key: 'help.onboarding.quiz.questions.q10.options.b', value: 1 },
        { key: 'help.onboarding.quiz.questions.q10.options.c', value: 2 },
        { key: 'help.onboarding.quiz.questions.q10.options.d', value: 3 },
      ],
      correctAnswer: 0
    },
    {
      id: 3,
      questionKey: 'help.onboarding.quiz.questions.q3.question',
      explanationKey: 'help.onboarding.quiz.questions.q3.explanation',
      options: [
        { key: 'help.onboarding.quiz.questions.q3.options.a', value: 0 },
        { key: 'help.onboarding.quiz.questions.q3.options.b', value: 1 },
        { key: 'help.onboarding.quiz.questions.q3.options.c', value: 2 },
        { key: 'help.onboarding.quiz.questions.q3.options.d', value: 3 },
      ],
      correctAnswer: 2
    },
    {
      id: 6,
      questionKey: 'help.onboarding.quiz.questions.q6.question',
      explanationKey: 'help.onboarding.quiz.questions.q6.explanation',
      options: [
        { key: 'help.onboarding.quiz.questions.q6.options.a', value: 0 },
        { key: 'help.onboarding.quiz.questions.q6.options.b', value: 1 },
        { key: 'help.onboarding.quiz.questions.q6.options.c', value: 2 },
        { key: 'help.onboarding.quiz.questions.q6.options.d', value: 3 },
      ],
      correctAnswer: 2
    },
    {
      id: 5,
      questionKey: 'help.onboarding.quiz.questions.q5.question',
      explanationKey: 'help.onboarding.quiz.questions.q5.explanation',
      options: [
        { key: 'help.onboarding.quiz.questions.q5.options.a', value: 0 },
        { key: 'help.onboarding.quiz.questions.q5.options.b', value: 1 },
        { key: 'help.onboarding.quiz.questions.q5.options.c', value: 2 },
        { key: 'help.onboarding.quiz.questions.q5.options.d', value: 3 },
      ],
      correctAnswer: 3
    },
    {
      id: 2,
      questionKey: 'help.onboarding.quiz.questions.q2.question',
      explanationKey: 'help.onboarding.quiz.questions.q2.explanation',
      options: [
        { key: 'help.onboarding.quiz.questions.q2.options.a', value: 0 },
        { key: 'help.onboarding.quiz.questions.q2.options.b', value: 1 },
        { key: 'help.onboarding.quiz.questions.q2.options.c', value: 2 },
        { key: 'help.onboarding.quiz.questions.q2.options.d', value: 3 },
      ],
      correctAnswer: 0
    },
    {
      id: 7,
      questionKey: 'help.onboarding.quiz.questions.q7.question',
      explanationKey: 'help.onboarding.quiz.questions.q7.explanation',
      options: [
        { key: 'help.onboarding.quiz.questions.q7.options.a', value: 0 },
        { key: 'help.onboarding.quiz.questions.q7.options.b', value: 1 },
        { key: 'help.onboarding.quiz.questions.q7.options.c', value: 2 },
        { key: 'help.onboarding.quiz.questions.q7.options.d', value: 3 },
      ],
      correctAnswer: 3
    },
    {
      id: 4,
      questionKey: 'help.onboarding.quiz.questions.q4.question',
      explanationKey: 'help.onboarding.quiz.questions.q4.explanation',
      options: [
        { key: 'help.onboarding.quiz.questions.q4.options.a', value: 0 },
        { key: 'help.onboarding.quiz.questions.q4.options.b', value: 1 },
        { key: 'help.onboarding.quiz.questions.q4.options.c', value: 2 },
        { key: 'help.onboarding.quiz.questions.q4.options.d', value: 3 },
      ],
      correctAnswer: 1
    },
    {
      id: 9,
      questionKey: 'help.onboarding.quiz.questions.q9.question',
      explanationKey: 'help.onboarding.quiz.questions.q9.explanation',
      options: [
        { key: 'help.onboarding.quiz.questions.q9.options.a', value: 0 },
        { key: 'help.onboarding.quiz.questions.q9.options.b', value: 1 },
        { key: 'help.onboarding.quiz.questions.q9.options.c', value: 2 },
        { key: 'help.onboarding.quiz.questions.q9.options.d', value: 3 },
      ],
      correctAnswer: 3
    },
    {
      id: 8,
      questionKey: 'help.onboarding.quiz.questions.q8.question',
      explanationKey: 'help.onboarding.quiz.questions.q8.explanation',
      options: [
        { key: 'help.onboarding.quiz.questions.q8.options.a', value: 0 },
        { key: 'help.onboarding.quiz.questions.q8.options.b', value: 1 },
        { key: 'help.onboarding.quiz.questions.q8.options.c', value: 2 },
        { key: 'help.onboarding.quiz.questions.q8.options.d', value: 3 },
      ],
      correctAnswer: 2
    },
  ];

  currentQuestionIndex = signal(0);
  userAnswers = signal<(number | null)[]>(new Array(this.questions.length).fill(null));
  showResults = signal(false);
  quizStarted = signal(false);

  currentQuestion = computed(() => this.questions[this.currentQuestionIndex()]);
  currentAnswer = computed(() => this.userAnswers()[this.currentQuestionIndex()]);

  score = computed(() => {
    let count = 0;
    const answers = this.userAnswers();
    for (let i = 0; i < answers.length; i++) {
      if (answers[i] === this.questions[i].correctAnswer) {
        count++;
      }
    }
    return count;
  });

  scorePercentage = computed(() => {
    const scoreValue = this.score();
    return Math.round((scoreValue / this.questions.length) * 100);
  });

  isLastQuestion = computed(() => this.currentQuestionIndex() === this.questions.length - 1);
  isAnswered = computed(() => this.currentAnswer() !== null);

  startQuiz(): void {
    this.quizStarted.set(true);
  }

  selectAnswer(value: number): void {
    // The correction is revealed as soon as an answer is given, so answers are
    // final: changing one afterwards would let the score be inflated.
    if (this.isAnswered()) {
      return;
    }
    const answers = [...this.userAnswers()];
    answers[this.currentQuestionIndex()] = value;
    this.userAnswers.set(answers);
  }

  nextQuestion(): void {
    if (this.isLastQuestion()) {
      this.showResults.set(true);
    } else {
      this.currentQuestionIndex.update(i => i + 1);
    }
  }

  previousQuestion(): void {
    if (this.currentQuestionIndex() > 0) {
      this.currentQuestionIndex.update(i => i - 1);
    }
  }

  restartQuiz(): void {
    this.currentQuestionIndex.set(0);
    this.userAnswers.set(new Array(this.questions.length).fill(null));
    this.showResults.set(false);
  }

  getOptionKey(question: QuizQuestion, value: number): string {
    return question.options.find(o => o.value === value)?.key ?? '';
  }

  getOptionState(value: number): OptionState {
    if (!this.isAnswered()) {
      return 'selectable';
    }
    if (value === this.currentQuestion().correctAnswer) {
      return 'correct';
    }
    return value === this.currentAnswer() ? 'incorrect' : 'dimmed';
  }

  getOptionClass(value: number): string {
    return OPTION_STATE_CLASSES[this.getOptionState(value)];
  }

  getResultMessage(): string {
    const percentage = this.scorePercentage();
    if (percentage >= 80) return 'help.onboarding.quiz.results.excellent';
    if (percentage >= 60) return 'help.onboarding.quiz.results.good';
    if (percentage >= 40) return 'help.onboarding.quiz.results.average';
    return 'help.onboarding.quiz.results.needsWork';
  }
}
